import { Avatar, Button, IconButton, Select } from "@design";
import { useQuery } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { listTeamsOptions } from "@/api";
import { Text } from "@/components/Text";
import type { MemberTeam, MemberUser } from "./MembershipPanel";

/**
 * The member/team rows + the add-team row for MembershipPanel. Split out for
 * the file-size convention. The role constants + helpers live here too since
 * both the rows and the panel use them.
 */

export const ROLES = ["viewer", "editor", "admin"] as const;
export type Role = (typeof ROLES)[number];
export const asRole = (v: string): Role => ((ROLES as readonly string[]).includes(v) ? (v as Role) : "viewer");

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0] ?? "")
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

/** A member's role: an editable select + remove button, or a read-only label. */
function RoleControls({
  name,
  role,
  editable,
  removeLabel,
  onRole,
  onRemove,
}: Readonly<{
  name: string;
  role: string;
  editable: boolean;
  removeLabel: string;
  onRole: (role: string) => void;
  onRemove: () => void;
}>) {
  const { t } = useTranslation("build");
  if (!editable) {
    return (
      <Text variant="caption" tone="tertiary">
        {t(`access.role.${asRole(role)}`)}
      </Text>
    );
  }
  return (
    <>
      <div className="w-32">
        <Select
          size="md"
          aria-label={t("access.changeRole", { name })}
          options={ROLES.map((r) => ({ value: r, label: t(`access.role.${r}`) }))}
          value={asRole(role)}
          onChange={onRole}
        />
      </div>
      <IconButton icon={<Trash2 />} aria-label={removeLabel} variant="ghost" size="sm" onClick={onRemove} />
    </>
  );
}

export function MemberRow({
  member,
  isYou,
  canManage,
  onRole,
  onRemove,
}: Readonly<{
  member: MemberUser;
  isYou: boolean;
  canManage: boolean;
  onRole: (role: string) => void;
  onRemove: () => void;
}>) {
  const { t } = useTranslation("build");
  return (
    <li className="flex items-center gap-3 py-2">
      <Avatar initials={initialsOf(member.name)} src={member.avatarUrl ?? undefined} size="sm" />
      <div className="flex min-w-0 flex-1 flex-col">
        <Text variant="body-sm" className="truncate">
          {isYou ? `${member.name} (${t("access.you")})` : member.name}
        </Text>
        <Text variant="caption" tone="tertiary" className="truncate">
          {member.email}
        </Text>
      </div>
      <RoleControls
        name={member.name}
        role={member.role}
        editable={canManage && !isYou}
        removeLabel={t("access.remove", { name: member.name })}
        onRole={onRole}
        onRemove={onRemove}
      />
    </li>
  );
}

export function TeamMemberRow({
  member,
  canManage,
  onRole,
  onRemove,
}: Readonly<{
  member: MemberTeam;
  canManage: boolean;
  onRole: (role: string) => void;
  onRemove: () => void;
}>) {
  const { t } = useTranslation("build");
  return (
    <li className="flex items-center gap-3 py-2">
      <Text variant="body-sm" className="flex-1 truncate">
        {member.name}
      </Text>
      <RoleControls
        name={member.name}
        role={member.role}
        editable={canManage}
        removeLabel={t("access.removeTeam", { name: member.name })}
        onRole={onRole}
        onRemove={onRemove}
      />
    </li>
  );
}

/** Add-team row: pick a team (from /teams) + a role, then add it. */
export function AddTeamRow({
  excludeUuids,
  onAdd,
  adding,
}: Readonly<{
  excludeUuids: string[];
  onAdd: (teamUuid: string, role: string) => void;
  adding?: boolean;
}>) {
  const { t } = useTranslation("build");
  const teams = useQuery(listTeamsOptions({ query: { page_size: 100 } }));
  const [teamUuid, setTeamUuid] = useState("");
  const [role, setRole] = useState<string>("viewer");

  const options = (teams.data?.data ?? []).filter((tm) => !excludeUuids.includes(tm.uuid));
  const add = () => {
    if (!teamUuid) return;
    onAdd(teamUuid, role);
    setTeamUuid("");
    setRole("viewer");
  };

  return (
    <div className="flex items-center gap-2 border-t border-border-subtle pt-3">
      <div className="flex-1">
        <Select
          size="md"
          aria-label={t("access.teamLabel")}
          options={options.map((tm) => ({ value: tm.uuid, label: tm.name }))}
          value={teamUuid}
          onChange={setTeamUuid}
          placeholder={t("access.teamPlaceholder")}
        />
      </div>
      <div className="w-32">
        <Select
          size="md"
          aria-label={t("access.role.label")}
          options={ROLES.map((r) => ({ value: r, label: t(`access.role.${r}`) }))}
          value={role}
          onChange={setRole}
        />
      </div>
      <Button variant="secondary" size="md" onClick={add} loading={adding} disabled={!teamUuid}>
        {t("access.addTeam")}
      </Button>
    </div>
  );
}
